/**
 * Family and account encryption keypair primitives.
 *
 * Family key: X25519 keypair generated once per org. The private key is
 * distributed to members via ECIES (`familyKeyWrap`) — see
 * `@heybesafe/shared` `decryptFamilyHandoff`.
 *
 * Account key: X25519 keypair per user. Used as the recipient key for
 * ECIES wrapping of the family private key (the `familyKeyWrap` bundle
 * on each org). Wrapped under the OPAQUE-derived accountWrappingKey for
 * server storage and under the PBKDF2-derived localWrappingKey for
 * offline unlock.
 */

import { base64UrlToBytes } from './base64url';

// The account-key helpers live in @heybesafe/shared so the browser extension
// can use them too (byte-identical). Re-exported here for existing web call
// sites. `generateAccountKeyPair` + `wrapAccountPrivateKey` moved there as part
// of adding account-key generation to the extension's register flow.
export {
  deriveAccountWrappingKey,
  unwrapAccountPrivateKey,
  localWrapAccountPrivateKey,
  localUnwrapAccountPrivateKey,
  generateAccountKeyPair,
  deriveAccountPublicKey,
  wrapAccountPrivateKey,
} from '@heybesafe/shared';

export async function generateFamilyKeyPair(): Promise<{
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}> {
  const keyPair = (await crypto.subtle.generateKey(
    { name: 'X25519' },
    true,
    ['deriveBits'],
  )) as CryptoKeyPair;

  const publicJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
  const privateJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey);

  // JWK 'x' is the public key, 'd' is the private key (base64url, no padding)
  return {
    publicKey: base64UrlToBytes(publicJwk.x!),
    privateKey: base64UrlToBytes(privateJwk.d!),
  };
}

// Account-level X25519 keypair generation + server-wrap now live in
// @heybesafe/shared (re-exported above) so the extension shares one
// byte-identical implementation.

